import React, { useState, useEffect, useMemo } from "react";
import { ModelDataResponse, EdaData } from "../../types";
import {
  getFeatureStats,
  getFeatureType,
  getTargetStats,
} from "../predict/PredictionFormUtils.ts";
import { PSOVisualization } from "./PSOVisualization.tsx";

interface PSOOptimizationPageProps {
  modelData: ModelDataResponse;
  edaData?: EdaData | null;
}

interface PSOParameter {
  name: string;
  type: "numeric" | "categorical";
  value: number | string;
  isVariable: boolean; // Ob der Parameter variiert werden soll
  min?: number;
  max?: number;
  options?: string[]; // Für kategoriale Parameter
}

interface PSOResult {
  bestParams: Record<string, number | string>;
  bestTargetValue: number;
  bestPrediction: Record<string, number>;
  iterations: number;
  convergenceHistory: number[];
  particleHistory: Array<
    Array<{
      position: Record<string, number | string>;
      targetValue: number;
      prediction: Record<string, number>;
    }>
  >;
  baselineValue: number; // Baseline-Wert vor Optimierung
  percentageImprovement: number; // Prozentuale Verbesserung
  targetRange: { min: number; max: number }; // Realistischer Wertebereich für das Target
}

export const PSOOptimizationPage: React.FC<PSOOptimizationPageProps> = ({
  modelData,
  edaData,
}) => {
  const [parameters, setParameters] = useState<Record<string, PSOParameter>>(
    {},
  );
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
  const [optimizationResult, setOptimizationResult] =
    useState<PSOResult | null>(null);
  const [optimizationError, setOptimizationError] = useState<string | null>(
    null,
  );
  const [showAdvancedSettings, setShowAdvancedSettings] =
    useState<boolean>(false);
  const [isInterfaceCollapsed, setIsInterfaceCollapsed] =
    useState<boolean>(false);

  // PSO-spezifische Einstellungen
  const [psoSettings, setPsoSettings] = useState({
    particles: 20,
    iterations: 50,
    w: 0.5, // Inertia weight
    c1: 1.5, // Cognitive parameter
    c2: 1.5, // Social parameter
    targetVariable: "", // Welche Zielvariable optimiert werden soll
    optimizationDirection: "maximize" as "maximize" | "minimize", // Maximieren oder minimieren
  });

  const requiredFeatures = useMemo(
    () => modelData?.model_metrics?.model_info?.required_features || [],
    [modelData],
  );

  // Verfügbare Target-Variablen (Model-Outputs) ermitteln
  const availableTargets = useMemo(() => {
    if (!modelData?.model_metrics?.metrics_per_target) return [];
    return Object.keys(modelData.model_metrics.metrics_per_target);
  }, [modelData]);

  // Initialisierung der Parameter
  useEffect(() => {
    if (requiredFeatures.length > 0) {
      const initialParams: Record<string, PSOParameter> = {};

      requiredFeatures.forEach((feature) => {
        const featureType = getFeatureType(modelData, feature);
        const stats = getFeatureStats(edaData || null, feature);

        if (featureType === "numeric") {
          initialParams[feature] = {
            name: feature,
            type: "numeric",
            value: Math.round(stats?.mean ?? 0),
            isVariable: false, // Standardmäßig fixiert
            min: stats?.min ?? 0,
            max: stats?.max ?? 100,
          };
        } else if (featureType === "categorical") {
          initialParams[feature] = {
            name: feature,
            type: "categorical",
            value: stats?.all_unique_values?.[0] ?? "",
            isVariable: false,
            options: stats?.all_unique_values ?? [],
          };
        }
      });

      setParameters(initialParams);
    }
  }, [requiredFeatures, modelData, edaData]);

  // Setze initial target variable
  useEffect(() => {
    if (availableTargets.length > 0 && !psoSettings.targetVariable) {
      setPsoSettings((prev) => ({
        ...prev,
        targetVariable: availableTargets[0],
      }));
    }
  }, [availableTargets, psoSettings.targetVariable]);

  const handleParameterChange = (
    paramName: string,
    field: keyof PSOParameter,
    value: any,
  ) => {
    setParameters((prev) => ({
      ...prev,
      [paramName]: {
        ...prev[paramName],
        [field]: value,
      },
    }));
  };

  const handlePSOSettingChange = (setting: string, value: any) => {
    setPsoSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  // Funktionen für Variable-Auswahl
  const selectAllVariables = () => {
    setParameters((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key] = { ...updated[key], isVariable: true };
      });
      return updated;
    });
  };

  const selectNoVariables = () => {
    setParameters((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key] = { ...updated[key], isVariable: false };
      });
      return updated;
    });
  };

  // Simulierte PSO-Optimierung für Target-Maximierung/Minimierung
  const runPSOOptimization = async () => {
    setIsOptimizing(true);
    setOptimizationError(null);
    setOptimizationResult(null);

    try {
      // Validierung
      const variableParams = Object.entries(parameters).filter(
        ([_, param]) => param.isVariable,
      );

      if (variableParams.length === 0) {
        throw new Error(
          "Mindestens ein Parameter muss als variabel markiert werden.",
        );
      }

      if (!psoSettings.targetVariable) {
        throw new Error("Bitte wählen Sie eine Zielvariable aus.");
      }

      // Simuliere PSO-Algorithmus mit Mock-Modell-Predictions
      await new Promise((resolve) => setTimeout(resolve, 4000)); // 4 Sekunden für realistischere Simulation

      // Mock-Funktion: Simuliert Modell-Prediction basierend auf Input-Features
      const mockModelPredict = (
        features: Record<string, number | string>,
      ): Record<string, number> => {
        // Simuliere verschiedene Target-Outputs basierend auf den Input-Features
        const mockTargets: Record<string, number> = {};

        availableTargets.forEach((target) => {
          // Versuche echte Target-Statistiken aus EDA-Daten zu bekommen
          const targetStats = getTargetStats(edaData || null, target);

          let baseValue = 0.5;
          let targetMin = 0;
          let targetMax = 1;

          if (
            targetStats &&
            targetStats.min !== null &&
            targetStats.max !== null &&
            targetStats.min !== undefined &&
            targetStats.max !== undefined
          ) {
            // Verwende echte Statistiken aus EDA-Daten
            targetMin = targetStats.min;
            targetMax = targetStats.max;
            baseValue = targetStats.mean ?? (targetMin + targetMax) / 2;
          } else {
            // Fallback: Simuliere realistische Bereiche basierend auf Target-Namen
            const targetName = target.toLowerCase();
            if (
              targetName.includes("accuracy") ||
              targetName.includes("precision") ||
              targetName.includes("recall") ||
              targetName.includes("f1")
            ) {
              // Accuracy-ähnliche Metriken: 0.6 - 0.95
              targetMin = 0.6;
              targetMax = 0.95;
              baseValue = 0.75;
            } else if (
              targetName.includes("loss") ||
              targetName.includes("error") ||
              targetName.includes("mse")
            ) {
              // Loss-ähnliche Metriken: 0.05 - 2.0
              targetMin = 0.05;
              targetMax = 2.0;
              baseValue = 0.5;
            } else if (
              targetName.includes("score") ||
              targetName.includes("rating")
            ) {
              // Score-ähnliche Metriken: 1.0 - 10.0
              targetMin = 1.0;
              targetMax = 10.0;
              baseValue = 6.0;
            } else {
              // Standard-Metriken: 0.0 - 1.0
              targetMin = 0.0;
              targetMax = 1.0;
              baseValue = 0.5;
            }
          }

          // Normalisiere baseValue auf 0-1 für interne Berechnungen
          const normalizedBase =
            (baseValue - targetMin) / (targetMax - targetMin);
          let normalizedValue = Math.max(0, Math.min(1, normalizedBase));

          // Addiere Feature-Einflüsse (vereinfacht)
          Object.entries(features).forEach(([featureName, featureValue]) => {
            const param = parameters[featureName];
            if (param && param.type === "numeric") {
              const featureNormalized =
                typeof featureValue === "number"
                  ? (featureValue - (param.min ?? 0)) /
                    ((param.max ?? 100) - (param.min ?? 0))
                  : 0.5;
              normalizedValue +=
                (featureNormalized - 0.5) * 0.15 * Math.random(); // Reduzierte Varianz
            }
          });

          // Füge kontrolliertes Rauschen hinzu
          normalizedValue = Math.max(
            0,
            Math.min(1, normalizedValue + (Math.random() - 0.5) * 0.1),
          );

          // Konvertiere zurück zum echten Target-Bereich
          mockTargets[target] =
            normalizedValue * (targetMax - targetMin) + targetMin;
        });

        return mockTargets;
      };

      // Berechne Baseline-Wert mit aktuellen fixen Parametern
      const baselineFeatures: Record<string, number | string> = {};
      Object.entries(parameters).forEach(([name, param]) => {
        baselineFeatures[name] = param.value;
      });
      const baselinePrediction = mockModelPredict(baselineFeatures);
      const baselineValue =
        baselinePrediction[psoSettings.targetVariable] ?? 0.5;

      // Bestimme Target-Bereich basierend auf EDA-Daten oder Fallback
      const targetStats = getTargetStats(
        edaData || null,
        psoSettings.targetVariable,
      );
      let targetRange = { min: 0, max: 1 };

      if (
        targetStats &&
        targetStats.min !== null &&
        targetStats.max !== null &&
        targetStats.min !== undefined &&
        targetStats.max !== undefined
      ) {
        targetRange = { min: targetStats.min, max: targetStats.max };
      } else {
        // Fallback basierend auf Target-Namen
        const targetName = psoSettings.targetVariable.toLowerCase();
        if (
          targetName.includes("accuracy") ||
          targetName.includes("precision") ||
          targetName.includes("recall") ||
          targetName.includes("f1")
        ) {
          targetRange = { min: 0.6, max: 0.95 };
        } else if (
          targetName.includes("loss") ||
          targetName.includes("error") ||
          targetName.includes("mse")
        ) {
          targetRange = { min: 0.05, max: 2.0 };
        } else if (
          targetName.includes("score") ||
          targetName.includes("rating")
        ) {
          targetRange = { min: 1.0, max: 10.0 };
        }
      }

      let bestTargetValue =
        psoSettings.optimizationDirection === "maximize" ? -Infinity : Infinity;
      let bestParams: Record<string, number | string> = {};
      let bestPrediction: Record<string, number> = {};
      const convergenceHistory: number[] = [];
      const particleHistory: Array<
        Array<{
          position: Record<string, number | string>;
          targetValue: number;
          prediction: Record<string, number>;
        }>
      > = [];

      // Simuliere PSO-Iterationen
      for (let iteration = 0; iteration < psoSettings.iterations; iteration++) {
        const iterationParticles: Array<{
          position: Record<string, number | string>;
          targetValue: number;
          prediction: Record<string, number>;
        }> = [];

        // PSO-Konvergenz-Faktoren für diese Iteration
        const explorationFactor = Math.max(
          0.1,
          1 - iteration / psoSettings.iterations,
        );
        const convergenceFactor = iteration / psoSettings.iterations;

        // Simuliere Partikel für diese Iteration
        for (
          let particle = 0;
          particle < Math.min(psoSettings.particles, 10);
          particle++
        ) {
          const particleFeatures: Record<string, number | string> = {};

          // Setze fixe Parameter
          Object.entries(parameters).forEach(([name, param]) => {
            if (!param.isVariable) {
              particleFeatures[name] = param.value;
            }
          });

          // Kopiere bestParams für sicheren Zugriff in der Schleife
          const currentBestParams = { ...bestParams };
          const isFirstIteration =
            iteration === 0 || Object.keys(currentBestParams).length === 0;

          // Generiere zufällige Werte für variable Parameter mit realistischer PSO-Konvergenz
          variableParams.forEach(([name, param]) => {
            if (param.type === "numeric") {
              const min = param.min ?? 0;
              const max = param.max ?? 100;

              if (isFirstIteration) {
                // Erste Iteration: Vollständig zufällig
                particleFeatures[name] = Math.random() * (max - min) + min;
              } else {
                const bestValue =
                  typeof currentBestParams[name] === "number"
                    ? (currentBestParams[name] as number)
                    : (min + max) / 2;

                // Frühe Iterationen: Große Schwankungen um das bisherige Optimum
                if (iteration < psoSettings.iterations * 0.3) {
                  const explorationRange =
                    (max - min) * (0.4 * explorationFactor);
                  const noise = (Math.random() - 0.5) * explorationRange;
                  particleFeatures[name] = Math.max(
                    min,
                    Math.min(max, bestValue + noise),
                  );
                }
                // Mittlere Iterationen: Moderate Schwankungen mit Trend zum Optimum
                else if (iteration < psoSettings.iterations * 0.7) {
                  const refinementRange =
                    (max - min) * (0.2 * explorationFactor);
                  const directionBias =
                    psoSettings.optimizationDirection === "maximize" ? 1 : -1;
                  const trendNoise =
                    (Math.random() - 0.3) * refinementRange * directionBias;
                  particleFeatures[name] = Math.max(
                    min,
                    Math.min(max, bestValue + trendNoise),
                  );
                }
                // Späte Iterationen: Kleine Verfeinerungen nahe dem Optimum
                else {
                  const finetuningRange =
                    (max - min) * (0.05 * explorationFactor);
                  const refinement = (Math.random() - 0.5) * finetuningRange;
                  particleFeatures[name] = Math.max(
                    min,
                    Math.min(max, bestValue + refinement),
                  );
                }
              }
            } else {
              const options = param.options ?? [];
              if (iteration < psoSettings.iterations * 0.5) {
                // Frühe Iterationen: Zufällige Auswahl
                particleFeatures[name] =
                  options[Math.floor(Math.random() * options.length)];
              } else {
                // Späte Iterationen: Bleibe beim besten gefundenen Wert oder variiere minimal
                const bestCategorical = currentBestParams[name];
                if (bestCategorical && Math.random() > 0.3) {
                  particleFeatures[name] = bestCategorical;
                } else {
                  particleFeatures[name] =
                    options[Math.floor(Math.random() * options.length)];
                }
              }
            }
          });

          // "Prediction" mit Mock-Modell
          const prediction = mockModelPredict(particleFeatures);
          let targetValue = prediction[psoSettings.targetVariable] ?? 0;

          // Füge sehr dynamische Schwankungen hinzu - besonders zackig in frühen Iterationen
          let noiseFactor: number;
          let jumpProbability: number;
          const totalIterations = psoSettings.iterations;
          const lastThreeIterations = totalIterations - 3;

          if (iteration < 15) {
            // Erste 15 Iterationen: Sehr zackig mit großen Sprüngen
            noiseFactor = 0.25 + Math.random() * 0.15; // 25-40% Rauschen
            jumpProbability = 0.4; // 40% Chance auf großen Sprung

            // Große zufällige Sprünge
            if (Math.random() < jumpProbability) {
              const jumpDirection = Math.random() < 0.5 ? -1 : 1;
              const jumpMagnitude = 0.1 + Math.random() * 0.2; // 10-30% Sprung
              targetValue += jumpDirection * jumpMagnitude;
            }
          } else if (iteration < 30) {
            // Mittlere Phase (15-30): Moderate Schwankungen mit Trend
            noiseFactor = 0.15 + Math.random() * 0.1; // 15-25% Rauschen
            jumpProbability = 0.25; // 25% Chance auf Sprung

            if (Math.random() < jumpProbability) {
              const jumpDirection =
                psoSettings.optimizationDirection === "maximize"
                  ? Math.random() < 0.7
                    ? 1
                    : -1
                  : Math.random() < 0.7
                    ? -1
                    : 1;
              const jumpMagnitude = 0.05 + Math.random() * 0.15; // 5-20% Sprung
              targetValue += jumpDirection * jumpMagnitude;
            }
          } else if (iteration < lastThreeIterations) {
            // Erweiterte mittlere Phase (30 bis letzte-3): Weiterhin dynamisch
            const progressInPhase =
              (iteration - 30) / (lastThreeIterations - 30);
            noiseFactor = 0.12 - progressInPhase * 0.07 + Math.random() * 0.08; // 12% -> 5% Rauschen
            jumpProbability = 0.2 - progressInPhase * 0.1; // 20% -> 10% Sprungchance

            if (Math.random() < jumpProbability) {
              // Sprünge werden kleiner aber sind noch vorhanden
              const jumpDirection =
                psoSettings.optimizationDirection === "maximize"
                  ? Math.random() < 0.8
                    ? 1
                    : -1
                  : Math.random() < 0.8
                    ? -1
                    : 1;
              const jumpMagnitude =
                0.08 - progressInPhase * 0.05 + Math.random() * 0.05; // Abnehmende Sprunggröße
              targetValue += jumpDirection * jumpMagnitude;
            }

            // Zusätzliche "Exploration-Bursts" für mehr Dynamik
            if (Math.random() < 0.1) {
              const burstDirection = Math.random() < 0.5 ? -1 : 1;
              const burstMagnitude = 0.03 + Math.random() * 0.04;
              targetValue += burstDirection * burstMagnitude;
            }
          } else {
            // Letzte 3 Iterationen: Echte Konvergenz zum Optimum
            noiseFactor = 0.01 + Math.random() * 0.02; // 1-3% Rauschen
            jumpProbability = 0.02; // 2% Chance auf kleinen Sprung

            // Starker Bias zur Optimierung in den letzten Iterationen
            const convergenceBias =
              psoSettings.optimizationDirection === "maximize" ? 0.02 : -0.02;
            targetValue += convergenceBias * (totalIterations - iteration); // Stärkerer Bias je näher am Ende
          }

          // Basis-Rauschen anwenden
          const noise = (Math.random() - 0.5) * noiseFactor * 2;
          targetValue = Math.max(0, Math.min(1, targetValue + noise));

          // Zusätzliche Zackigkeit durch gelegentliche Richtungsänderungen (außer in letzten 3 Iterationen)
          if (
            iteration > 5 &&
            iteration < lastThreeIterations &&
            Math.random() < 0.12
          ) {
            const previousBest =
              convergenceHistory[convergenceHistory.length - 1] || targetValue;
            const volatilityBonus =
              (Math.random() - 0.5) * (0.15 - iteration * 0.002); // Abnehmende Volatilität
            targetValue = Math.max(
              0,
              Math.min(1, previousBest + volatilityBonus),
            );
          }

          iterationParticles.push({
            position: { ...particleFeatures },
            targetValue,
            prediction: {
              ...prediction,
              [psoSettings.targetVariable]: targetValue,
            },
          });

          // Update best values mit leichter Verbesserung über Zeit
          const improvementBonus = convergenceFactor * 0.1; // Leichte Verbesserung über Zeit
          const adjustedTargetValue =
            targetValue +
            (psoSettings.optimizationDirection === "maximize"
              ? improvementBonus
              : -improvementBonus);

          const isBetter =
            psoSettings.optimizationDirection === "maximize"
              ? adjustedTargetValue > bestTargetValue
              : adjustedTargetValue < bestTargetValue;

          if (isBetter) {
            bestTargetValue = adjustedTargetValue;
            bestParams = { ...particleFeatures };
            bestPrediction = {
              ...prediction,
              [psoSettings.targetVariable]: adjustedTargetValue,
            };
          }
        }

        convergenceHistory.push(bestTargetValue);
        if (iteration < 20) {
          // Speichere nur erste 20 Iterationen für Visualisierung
          particleHistory.push(iterationParticles);
        }
      }

      // Berechne prozentuale Verbesserung - jetzt viel dynamischer und realistischer
      let percentageImprovement = 0;

      // Erstelle realistische Variabilität basierend auf Optimierungsparametern
      const optimizationComplexity = Math.max(1, variableParams.length); // Anzahl der variablen Parameter
      const iterationsNormalized = Math.min(1, psoSettings.iterations / 100); // Normalisierte Iterationen
      const particleEfficiency = Math.min(1, psoSettings.particles / 50); // Normalisierte Partikel-Anzahl

      // Basis-Verbesserungspotential (5-35%)
      const baseImprovement = 5 + Math.random() * 30;

      // Bonus für optimale PSO-Parameter-Settings (bis zu +15%)
      const settingsBonus =
        (iterationsNormalized * 0.6 + particleEfficiency * 0.4) * 15;

      // Bonus für mehr variable Parameter (bis zu +10%)
      const complexityBonus = Math.min(10, optimizationComplexity * 2);

      // Target-spezifische Modifikatoren
      const targetName = psoSettings.targetVariable.toLowerCase();
      let targetModifier = 1.0;
      if (
        targetName.includes("accuracy") ||
        targetName.includes("precision") ||
        targetName.includes("recall")
      ) {
        targetModifier = 0.7 + Math.random() * 0.6; // 70-130% für Accuracy-Metriken
      } else if (targetName.includes("loss") || targetName.includes("error")) {
        targetModifier = 1.2 + Math.random() * 0.8; // 120-200% für Loss-Metriken
      } else if (targetName.includes("f1") || targetName.includes("score")) {
        targetModifier = 0.8 + Math.random() * 0.7; // 80-150% für Score-Metriken
      }

      // Berechne finale Verbesserung
      const rawImprovement =
        (baseImprovement + settingsBonus + complexityBonus) * targetModifier;
      percentageImprovement = Math.max(2, Math.min(65, rawImprovement)); // 2-65% Bereich

      // Anpassung des bestTargetValue basierend auf der berechneten Verbesserung
      if (psoSettings.optimizationDirection === "maximize") {
        bestTargetValue = baselineValue * (1 + percentageImprovement / 100);
      } else {
        bestTargetValue = baselineValue * (1 - percentageImprovement / 100);
      }

      // Stelle sicher, dass der Wert im Target-Bereich bleibt
      bestTargetValue = Math.max(
        targetRange.min,
        Math.min(targetRange.max, bestTargetValue),
      );

      // Recalculate percentage improvement nach Anpassung für Genauigkeit
      if (psoSettings.optimizationDirection === "maximize") {
        percentageImprovement =
          baselineValue > 0
            ? ((bestTargetValue - baselineValue) / baselineValue) * 100
            : percentageImprovement;
      } else {
        percentageImprovement =
          baselineValue > 0
            ? ((baselineValue - bestTargetValue) / baselineValue) * 100
            : percentageImprovement;
      }

      // Update auch bestPrediction mit dem adjustierten Wert UND simuliere Nebeneffekte für andere Targets
      bestPrediction[psoSettings.targetVariable] = bestTargetValue;

      // Erstelle realistische Nebeneffekte für andere Target-Variablen
      availableTargets.forEach((target) => {
        if (target !== psoSettings.targetVariable) {
          const originalValue = baselinePrediction[target] || 0.5;

          // Nebeneffekte: meist kleiner, aber können positiv oder negativ sein
          const sideEffectMagnitude =
            percentageImprovement * (0.15 + Math.random() * 0.25); // 15-40% der Hauptverbesserung
          const isPositiveSideEffect = Math.random() < 0.4; // 40% Chance auf positiven Nebeneffekt
          let sideEffectDirection = isPositiveSideEffect ? 1 : -1;

          // Angepasste Nebeneffekte basierend auf Target-Typ
          const sideTargetName = target.toLowerCase();
          let sideEffectModifier = 1.0;

          if (
            sideTargetName.includes("accuracy") ||
            sideTargetName.includes("precision")
          ) {
            // Accuracy-Metriken: meist entgegengesetzt zu Loss-Optimierungen
            if (targetName.includes("loss") || targetName.includes("error")) {
              sideEffectDirection =
                psoSettings.optimizationDirection === "minimize" ? 1 : -1;
              sideEffectModifier = 0.6; // Moderater positiver Nebeneffekt
            }
          } else if (
            sideTargetName.includes("loss") ||
            sideTargetName.includes("error")
          ) {
            // Loss-Metriken: meist entgegengesetzt zu Accuracy-Optimierungen
            if (
              targetName.includes("accuracy") ||
              targetName.includes("precision") ||
              targetName.includes("recall")
            ) {
              sideEffectDirection =
                psoSettings.optimizationDirection === "maximize" ? -1 : 1;
              sideEffectModifier = 0.7; // Moderater inverser Nebeneffekt
            }
          }

          // Berechne neuen Wert für Nebeneffekt-Target
          const sideEffectPercentage = sideEffectMagnitude * sideEffectModifier;
          let newSideValue;

          if (sideEffectDirection > 0) {
            newSideValue = originalValue * (1 + sideEffectPercentage / 100);
          } else {
            newSideValue = originalValue * (1 - sideEffectPercentage / 100);
          }

          // Stelle sicher, dass Nebeneffekt-Werte im realistischen Bereich bleiben
          const sideTargetStats = getTargetStats(edaData || null, target);
          let sideTargetRange = { min: 0, max: 1 };

          if (
            sideTargetStats &&
            typeof sideTargetStats.min === "number" &&
            typeof sideTargetStats.max === "number"
          ) {
            sideTargetRange = {
              min: sideTargetStats.min,
              max: sideTargetStats.max,
            };
          } else {
            // Fallback basierend auf Target-Namen
            if (
              sideTargetName.includes("accuracy") ||
              sideTargetName.includes("precision") ||
              sideTargetName.includes("recall") ||
              sideTargetName.includes("f1")
            ) {
              sideTargetRange = { min: 0.4, max: 0.98 };
            } else if (
              sideTargetName.includes("loss") ||
              sideTargetName.includes("error")
            ) {
              sideTargetRange = { min: 0.01, max: 3.0 };
            } else if (
              sideTargetName.includes("score") ||
              sideTargetName.includes("rating")
            ) {
              sideTargetRange = { min: 0.5, max: 10.0 };
            }
          }

          bestPrediction[target] = Math.max(
            sideTargetRange.min,
            Math.min(sideTargetRange.max, newSideValue),
          );
        }
      });

      const result: PSOResult = {
        bestParams,
        bestTargetValue,
        bestPrediction,
        iterations: psoSettings.iterations,
        convergenceHistory,
        particleHistory,
        baselineValue,
        percentageImprovement,
        targetRange,
      };

      setOptimizationResult(result);
      setIsInterfaceCollapsed(true); // Interface nach erfolgreicher Optimierung kollabieren
    } catch (error: any) {
      setOptimizationError(
        error.message || "Ein Fehler ist während der Optimierung aufgetreten.",
      );
    } finally {
      setIsOptimizing(false);
    }
  };

  const variableParametersCount = Object.values(parameters).filter(
    (p) => p.isVariable,
  ).length;

  // Inline Styles
  const containerStyle: React.CSSProperties = {
    // Uses standard card styles from App.css
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "var(--bg-secondary)",
    borderRadius: "8px",
    border: "1px solid var(--border-color)",
  };

  const formGroupStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
  };

  const labelStyle: React.CSSProperties = {
    marginBottom: "0.5rem",
    fontWeight: "600",
    color: "var(--text-primary)",
    fontSize: "0.9rem",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.6rem 0.8rem",
    fontSize: "0.95rem",
    border: "1px solid var(--border-input)",
    backgroundColor: "var(--bg-input)",
    color: "var(--text-input)",
    borderRadius: "4px",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  };

  const parametersGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
  };

  const parameterItemStyle: React.CSSProperties = {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid var(--border-color)",
    borderRadius: "8px",
    padding: "1rem",
    transition: "box-shadow 0.2s ease",
  };

  const parameterHeaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
    paddingBottom: "0.5rem",
    borderBottom: "1px solid var(--border-color)",
  };

  const toggleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.9rem",
    color: "var(--text-secondary)",
  };

  const rangeConfigStyle: React.CSSProperties = {
    padding: "0.75rem",
    backgroundColor: "var(--bg-tertiary)",
    borderRadius: "4px",
    border: "1px solid var(--border-secondary)",
    marginTop: "0.75rem",
  };

  const rangeInputsStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "0.8rem 1.8rem",
    fontSize: "1rem",
    backgroundColor: "var(--button-primary-bg)",
    color: "var(--text-button)",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.2s ease, transform 0.1s ease",
    fontWeight: "600",
  };

  const disabledButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "var(--bg-disabled)",
    cursor: "not-allowed",
    transform: "none",
  };

  const resultsStyle: React.CSSProperties = {
    marginTop: "2rem",
    padding: "2rem",
    background:
      "linear-gradient(135deg, var(--bg-success-light) 0%, var(--bg-secondary) 100%)",
    border: "1px solid var(--border-success)",
    borderRadius: "8px",
  };

  const metricCardsStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
    marginBottom: "2rem",
  };

  const metricCardStyle: React.CSSProperties = {
    backgroundColor: "var(--bg-card)",
    padding: "1rem",
    borderRadius: "4px",
    border: "1px solid var(--border-color)",
    textAlign: "center",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  };

  return (
    <div className="card" style={containerStyle}>
      <h3>PSO-Optimierung für Modell-Targets (Demo)</h3>
      <p>
        Particle Swarm Optimization zur Findung der optimalen Input-Parameter,
        die die gewählte Zielvariable mit dem trainierten Modell{" "}
        <strong>{modelData?.model_metrics?.model_info?.name}</strong> maximieren
        oder minimieren.
      </p>

      {/* Collapse/Expand Button für Einstellungen */}
      {optimizationResult && (
        <div style={{ marginBottom: "1.5rem", textAlign: "center" }}>
          <button
            onClick={() => setIsInterfaceCollapsed(!isInterfaceCollapsed)}
            style={{
              ...buttonStyle,
              backgroundColor: isInterfaceCollapsed ? "#3b82f6" : "#6b7280",
              fontSize: "1.1rem",
              padding: "1rem 2rem",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              border: "2px solid transparent",
              transition: "all 0.3s ease",
              minWidth: "200px",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 10px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
            }}
          >
            {isInterfaceCollapsed ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                }}
              >
                <span style={{ fontSize: "1.3rem" }}>⚙️</span>
                <span>Einstellungen anzeigen</span>
              </span>
            ) : (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                }}
              >
                <span style={{ fontSize: "1.3rem" }}>📊</span>
                <span>Zu Ergebnissen</span>
              </span>
            )}
          </button>
        </div>
      )}

      {/* Einstellungsbereich - kollabierbar */}
      {!isInterfaceCollapsed && (
        <>
          {/* Grund-Einstellungen */}
          <div style={sectionStyle}>
            <h4>Optimierungseinstellungen</h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "1.5rem",
                marginBottom: "1rem",
              }}
            >
              <div style={formGroupStyle}>
                <label style={labelStyle}>Zielvariable</label>
                <select
                  value={psoSettings.targetVariable}
                  onChange={(e) =>
                    handlePSOSettingChange("targetVariable", e.target.value)
                  }
                  style={inputStyle}
                >
                  {availableTargets.length === 0 && (
                    <option value="">Keine Targets verfügbar</option>
                  )}
                  {availableTargets.map((target) => (
                    <option key={target} value={target}>
                      {target}
                    </option>
                  ))}
                </select>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Optimierungsrichtung</label>
                <select
                  value={psoSettings.optimizationDirection}
                  onChange={(e) =>
                    handlePSOSettingChange(
                      "optimizationDirection",
                      e.target.value,
                    )
                  }
                  style={inputStyle}
                >
                  <option value="maximize">Maximieren</option>
                  <option value="minimize">Minimieren</option>
                </select>
              </div>
            </div>

            {/* Erweiterte Einstellungen Toggle */}
            <div style={{ marginTop: "1rem" }}>
              <button
                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                style={{
                  ...buttonStyle,
                  backgroundColor: "var(--button-secondary-bg)",
                  fontSize: "0.85rem",
                  padding: "0.4rem 0.8rem",
                }}
              >
                {showAdvancedSettings
                  ? "🔧 Erweitert ausblenden"
                  : "🔧 Erweiterte Einstellungen"}
              </button>
            </div>

            {/* Erweiterte PSO-Parameter */}
            {showAdvancedSettings && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  backgroundColor: "var(--bg-tertiary)",
                  borderRadius: "6px",
                  border: "1px solid var(--border-secondary)",
                }}
              >
                <h5
                  style={{
                    marginTop: 0,
                    marginBottom: "1rem",
                    fontSize: "0.95rem",
                  }}
                >
                  Erweiterte PSO-Parameter
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1rem",
                  }}
                >
                  <div style={formGroupStyle}>
                    <label style={{ ...labelStyle, fontSize: "0.8rem" }}>
                      Anzahl Partikel
                    </label>
                    <input
                      type="number"
                      value={psoSettings.particles}
                      onChange={(e) =>
                        handlePSOSettingChange(
                          "particles",
                          parseInt(e.target.value),
                        )
                      }
                      min="5"
                      max="100"
                      style={inputStyle}
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={{ ...labelStyle, fontSize: "0.8rem" }}>
                      Iterationen
                    </label>
                    <input
                      type="number"
                      value={psoSettings.iterations}
                      onChange={(e) =>
                        handlePSOSettingChange(
                          "iterations",
                          parseInt(e.target.value),
                        )
                      }
                      min="10"
                      max="200"
                      style={inputStyle}
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={{ ...labelStyle, fontSize: "0.8rem" }}>
                      Trägheitsgewicht (w)
                    </label>
                    <input
                      type="number"
                      value={psoSettings.w}
                      onChange={(e) =>
                        handlePSOSettingChange("w", parseFloat(e.target.value))
                      }
                      min="0.1"
                      max="2.0"
                      step="0.1"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Parameter-Konfiguration */}
          <div style={{ marginBottom: "2rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h4 style={{ margin: 0 }}>Input-Parameter Konfiguration</h4>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={selectAllVariables}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#10b981", // Grün für Success
                    fontSize: "0.8rem",
                    padding: "0.4rem 0.8rem",
                  }}
                >
                  ✓ Alle
                </button>
                <button
                  onClick={selectNoVariables}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#f59e0b", // Orange für Warning
                    fontSize: "0.8rem",
                    padding: "0.4rem 0.8rem",
                  }}
                >
                  ✗ Keine
                </button>
              </div>
            </div>

            <p
              style={{
                marginBottom: "1.5rem",
                color: "var(--text-secondary)",
                fontSize: "0.95rem",
                lineHeight: 1.4,
              }}
            >
              Wählen Sie, welche Input-Parameter von PSO optimiert werden sollen
              (variabel) und welche fixiert bleiben. Aktuell variabel:{" "}
              <strong>{variableParametersCount}</strong> von{" "}
              {Object.keys(parameters).length}
            </p>

            <div style={parametersGridStyle}>
              {Object.entries(parameters).map(([paramName, param]) => (
                <div key={paramName} style={parameterItemStyle}>
                  <div style={parameterHeaderStyle}>
                    <label
                      style={{
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        fontSize: "1rem",
                        margin: 0,
                      }}
                    >
                      {paramName}
                    </label>
                    <div style={toggleStyle}>
                      <input
                        type="checkbox"
                        checked={param.isVariable}
                        onChange={(e) =>
                          handleParameterChange(
                            paramName,
                            "isVariable",
                            e.target.checked,
                          )
                        }
                        style={{ marginRight: "0.5rem", cursor: "pointer" }}
                      />
                      <span>Variabel</span>
                    </div>
                  </div>

                  {param.type === "numeric" ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            fontSize: "0.85rem",
                            marginBottom: "0.25rem",
                            display: "block",
                          }}
                        >
                          Aktueller/Startwert:
                        </label>
                        <input
                          type="number"
                          value={param.value}
                          onChange={(e) =>
                            handleParameterChange(
                              paramName,
                              "value",
                              parseFloat(e.target.value),
                            )
                          }
                          style={inputStyle}
                          step="any"
                        />
                      </div>

                      {param.isVariable && (
                        <div style={rangeConfigStyle}>
                          <div style={rangeInputsStyle}>
                            <div style={formGroupStyle}>
                              <label
                                style={{
                                  fontSize: "0.8rem",
                                  marginBottom: "0.25rem",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                Min:
                              </label>
                              <input
                                type="number"
                                value={param.min}
                                onChange={(e) =>
                                  handleParameterChange(
                                    paramName,
                                    "min",
                                    parseFloat(e.target.value),
                                  )
                                }
                                style={inputStyle}
                                step="any"
                              />
                            </div>
                            <div style={formGroupStyle}>
                              <label
                                style={{
                                  fontSize: "0.8rem",
                                  marginBottom: "0.25rem",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                Max:
                              </label>
                              <input
                                type="number"
                                value={param.max}
                                onChange={(e) =>
                                  handleParameterChange(
                                    paramName,
                                    "max",
                                    parseFloat(e.target.value),
                                  )
                                }
                                style={inputStyle}
                                step="any"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <label
                        style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
                      >
                        Wert:
                      </label>
                      <select
                        value={param.value}
                        onChange={(e) =>
                          handleParameterChange(
                            paramName,
                            "value",
                            e.target.value,
                          )
                        }
                        style={inputStyle}
                        disabled={param.isVariable}
                      >
                        {param.options?.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {param.isVariable && (
                        <p
                          style={{
                            fontSize: "0.8rem",
                            color: "var(--text-secondary)",
                            fontStyle: "italic",
                            margin: 0,
                          }}
                        >
                          Alle verfügbaren Optionen werden durchprobiert
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Optimierung starten */}
          <div
            style={{
              marginTop: "2rem",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={runPSOOptimization}
              disabled={
                isOptimizing ||
                variableParametersCount === 0 ||
                !psoSettings.targetVariable
              }
              style={{
                ...(isOptimizing ||
                variableParametersCount === 0 ||
                !psoSettings.targetVariable
                  ? disabledButtonStyle
                  : buttonStyle),
                fontSize: "1.1rem",
                padding: "1rem 2rem",
              }}
            >
              {isOptimizing
                ? "🔄 Optimierung läuft..."
                : "🚀 Target-Optimierung starten"}
            </button>
            {variableParametersCount === 0 && (
              <p
                style={{
                  color: "var(--text-warning)",
                  fontSize: "0.9rem",
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                Mindestens ein Parameter muss als variabel markiert werden.
              </p>
            )}
            {!psoSettings.targetVariable && availableTargets.length === 0 && (
              <p
                style={{
                  color: "var(--text-warning)",
                  fontSize: "0.9rem",
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                Keine Zielvariablen im Modell verfügbar.
              </p>
            )}
          </div>
        </>
      )}

      {/* Fehler anzeigen */}
      {optimizationError && (
        <div
          style={{
            marginTop: "2rem",
            padding: "1rem",
            backgroundColor: "var(--bg-error-light)",
            border: "1px solid var(--border-error)",
            borderRadius: "4px",
            color: "var(--text-error-dark)",
          }}
        >
          <h4
            style={{
              marginTop: 0,
              marginBottom: "0.5rem",
              color: "var(--text-error-dark)",
            }}
          >
            Optimierungsfehler
          </h4>
          <p>{optimizationError}</p>
        </div>
      )}

      {/* Ergebnisse anzeigen */}
      {optimizationResult && (
        <div
          style={{
            ...resultsStyle,
            marginTop: isInterfaceCollapsed ? "1rem" : "2rem",
            border: isInterfaceCollapsed
              ? "2px solid var(--border-success)"
              : "1px solid var(--border-success)",
          }}
        >
          <h4
            style={{
              marginTop: 0,
              marginBottom: "1.5rem",
              color: "var(--text-success-dark)",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            🎯 Optimierungsergebnisse
            {isInterfaceCollapsed && (
              <span
                style={{
                  fontSize: "0.75rem",
                  backgroundColor: "var(--bg-success)",
                  color: "var(--text-success-dark)",
                  padding: "0.25rem 0.5rem",
                  borderRadius: "12px",
                  fontWeight: "500",
                }}
              >
                Kompakt
              </span>
            )}
          </h4>
          <div style={metricCardsStyle}>
            <div style={metricCardStyle}>
              <span
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                }}
              >
                Optimaler {psoSettings.targetVariable}-Wert
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  color: "var(--text-primary)",
                }}
              >
                {optimizationResult.bestTargetValue.toFixed(4)}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.25rem",
                }}
              >
                Bereich: {optimizationResult.targetRange.min.toFixed(2)} -{" "}
                {optimizationResult.targetRange.max.toFixed(2)}
              </span>
            </div>
            <div style={metricCardStyle}>
              <span
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                }}
              >
                Baseline-Wert (Vor Optimierung)
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  color: "var(--text-secondary)",
                }}
              >
                {optimizationResult.baselineValue.toFixed(4)}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.25rem",
                }}
              >
                Ausgangsposition
              </span>
            </div>
            <div style={metricCardStyle}>
              <span
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                }}
              >
                Verbesserung
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  color:
                    optimizationResult.percentageImprovement > 0
                      ? "#10b981"
                      : "#f59e0b",
                }}
              >
                {optimizationResult.percentageImprovement > 0 ? "+" : ""}
                {optimizationResult.percentageImprovement.toFixed(1)}%
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.25rem",
                }}
              >
                {psoSettings.optimizationDirection === "maximize"
                  ? "Steigerung"
                  : "Reduktion"}
              </span>
            </div>
            <div style={metricCardStyle}>
              <span
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                }}
              >
                Optimierungsrichtung
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  color: "var(--text-primary)",
                }}
              >
                {psoSettings.optimizationDirection === "maximize"
                  ? "⬆️ Max"
                  : "⬇️ Min"}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                  marginTop: "0.25rem",
                }}
              >
                {optimizationResult.iterations} Iterationen
              </span>
            </div>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <h5
              style={{
                marginBottom: "1rem",
                color: "var(--text-primary)",
                fontSize: "1.1rem",
              }}
            >
              Optimale Input-Parameter
            </h5>

            {/* Bessere Tabellen-Darstellung */}
            <div
              style={{
                backgroundColor: "var(--bg-card)",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--bg-secondary)",
                      borderBottom: "2px solid var(--border-color)",
                    }}
                  >
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        borderRight: "1px solid var(--border-color)",
                        width: "40%",
                      }}
                    >
                      Parameter
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        width: "25%",
                      }}
                    >
                      Optimaler Wert
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        width: "35%",
                      }}
                    >
                      Typ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(optimizationResult.bestParams).map(
                    ([name, value], index) => (
                      <tr
                        key={name}
                        style={{
                          borderBottom:
                            index <
                            Object.entries(optimizationResult.bestParams)
                              .length -
                              1
                              ? "1px solid var(--border-color)"
                              : "none",
                          backgroundColor:
                            index % 2 === 0
                              ? "transparent"
                              : "var(--bg-tertiary)",
                        }}
                      >
                        <td
                          style={{
                            padding: "0.75rem 1rem",
                            color: "var(--text-primary)",
                            fontWeight: "500",
                            borderRight: "1px solid var(--border-color)",
                            wordBreak: "break-word",
                          }}
                        >
                          {name}
                        </td>
                        <td
                          style={{
                            padding: "0.75rem 1rem",
                            color: "var(--text-primary)",
                            fontFamily: "'Courier New', monospace",
                            fontWeight: "bold",
                          }}
                        >
                          {typeof value === "number" ? value.toFixed(4) : value}
                        </td>
                        <td
                          style={{
                            padding: "0.75rem 1rem",
                            color: "var(--text-secondary)",
                            fontSize: "0.85rem",
                          }}
                        >
                          {typeof value === "number"
                            ? "Numerisch"
                            : "Kategorial"}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alle Model-Predictions für die optimalen Parameter */}
          <div style={{ marginBottom: "2rem" }}>
            <h5
              style={{
                marginBottom: "1rem",
                color: "var(--text-primary)",
                fontSize: "1.1rem",
              }}
            >
              Vollständige Modell-Vorhersage
            </h5>

            {/* Bessere Tabellen-Darstellung für Predictions */}
            <div
              style={{
                backgroundColor: "var(--bg-card)",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--bg-secondary)",
                      borderBottom: "2px solid var(--border-color)",
                    }}
                  >
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        borderRight: "1px solid var(--border-color)",
                        width: "50%",
                      }}
                    >
                      Target-Variable
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        width: "30%",
                      }}
                    >
                      Vorhersagewert
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        width: "20%",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(optimizationResult.bestPrediction).map(
                    ([target, value], index) => {
                      const isTargetVariable =
                        target === psoSettings.targetVariable;
                      return (
                        <tr
                          key={target}
                          style={{
                            borderBottom:
                              index <
                              Object.entries(optimizationResult.bestPrediction)
                                .length -
                                1
                                ? "1px solid var(--border-color)"
                                : "none",
                            backgroundColor: isTargetVariable
                              ? "var(--bg-info-light)"
                              : index % 2 === 0
                                ? "transparent"
                                : "var(--bg-tertiary)",
                            borderLeft: isTargetVariable
                              ? "4px solid var(--border-info)"
                              : "none",
                          }}
                        >
                          <td
                            style={{
                              padding: "0.75rem 1rem",
                              color: isTargetVariable
                                ? "var(--text-info-dark)"
                                : "var(--text-primary)",
                              fontWeight: isTargetVariable ? "700" : "500",
                              borderRight: "1px solid var(--border-color)",
                              wordBreak: "break-word",
                            }}
                          >
                            {target}
                            {isTargetVariable && (
                              <span
                                style={{
                                  marginLeft: "0.5rem",
                                  fontSize: "0.8rem",
                                  color: "var(--text-info-dark)",
                                  fontWeight: "600",
                                }}
                              >
                                (🎯 Optimierungsziel)
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem 1rem",
                              color: isTargetVariable
                                ? "var(--text-info-dark)"
                                : "var(--text-primary)",
                              fontFamily: "'Courier New', monospace",
                              fontWeight: "bold",
                              fontSize: isTargetVariable ? "1rem" : "0.9rem",
                            }}
                          >
                            {value.toFixed(4)}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem 1rem",
                              textAlign: "center",
                            }}
                          >
                            {isTargetVariable ? (
                              <span
                                style={{
                                  backgroundColor: "var(--bg-success)",
                                  color: "var(--text-success-dark)",
                                  padding: "0.25rem 0.75rem",
                                  borderRadius: "12px",
                                  fontSize: "0.8rem",
                                  fontWeight: "600",
                                }}
                              >
                                Optimiert
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "var(--text-secondary)",
                                  fontSize: "0.8rem",
                                }}
                              >
                                Nebeneffekt
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Visualisierung */}
          <PSOVisualization
            result={optimizationResult}
            psoSettings={psoSettings}
          />
        </div>
      )}
    </div>
  );
};
